import RwCloudConvertService from '@advisor/rwSdk/CloudConvertService';
import Documents from '@advisor/models/store/settings/Documents';
import exceptionHandler from '@advisor/util/exceptionHandler';
import {addRiaComponentTo} from '@advisor/util/store/paramInjects';

const handledResponses = new Set([409]);

export const documentState = () => new Documents();

const getters = {};

const actions = {
  async getDocuments({commit, rootState}) {
    try {
      const {riaId} = rootState;

      const fullPayload = addRiaComponentTo({riaId}, rootState.settings.default);
      const sdkResponse = await RwCloudConvertService.getDocVaultDocuments(fullPayload).catch((error) => {
        throw error;
      });

      commit('SET_DOCUMENTS', sdkResponse);

      return Promise.resolve(sdkResponse);
    } catch (error) {
      exceptionHandler.handleGenericException('documents.getDocuments', error);

      return Promise.reject(error);
    }
  },
  async getDocDetails({rootState}, {docId, ...payload}) {
    try {
      const {riaId} = rootState;
      const {format} = payload;
      const sdkRequest = {riaId, docId, format};
      const fullPayload = addRiaComponentTo(sdkRequest, rootState.settings.default);
      const sdkResponse = await RwCloudConvertService.getDocVaultDocument(fullPayload).catch((error) => {
        const {microserviceStatusCode, message} = error;

        if (handledResponses.has(microserviceStatusCode)) {
          // eslint-disable-next-line no-param-reassign
          error.response = {status: microserviceStatusCode, message};
        }

        throw error;
      });

      return Promise.resolve(sdkResponse);
    } catch (error) {
      if (!error.response || (error.response && error.response.status !== 409)) {
        exceptionHandler.handleGenericException('documents.getDocDetails', error);
      }

      return Promise.reject(error);
    }
  },
  async deleteDocument({rootState}, {docId}) {
    try {
      const {riaId} = rootState;
      const sdkRequest = {riaId, docId};
      const fullPayload = addRiaComponentTo(sdkRequest, rootState.settings.default);
      const sdkResponse = await RwCloudConvertService.deleteDocVaultDocument(fullPayload).catch((error) => {
        const {microserviceStatusCode, message} = error;

        if (handledResponses.has(microserviceStatusCode)) {
          // eslint-disable-next-line no-param-reassign
          error.response = {status: microserviceStatusCode, message};
        }

        throw error;
      });

      return Promise.resolve(sdkResponse);
    } catch (error) {
      if (!error.response || (error.response && error.response.status !== 409)) {
        exceptionHandler.handleGenericException('documents.deleteDocument', error);
      }

      return Promise.reject(error);
    }
  },
  async saveDocument({rootState}, payload) {
    try {
      const {riaId} = rootState;
      const sdkRequest = {riaId, payload};
      const fullPayload = addRiaComponentTo(sdkRequest, rootState.settings.default);
      const sdkResponse = await RwCloudConvertService.createDocVaultDocument(fullPayload).catch((error) => {
        const {microserviceStatusCode, message} = error;

        if (handledResponses.has(microserviceStatusCode)) {
          // eslint-disable-next-line no-param-reassign
          error.response = {status: microserviceStatusCode, message};
        }

        throw error;
      });

      return Promise.resolve(sdkResponse);
    } catch (error) {
      if (!error.response || (error.response && error.response.status !== 409)) {
        exceptionHandler.handleGenericException('documents.saveDocument', error);
      }

      return Promise.reject(error);
    }
  },
  async editDocument({rootState}, {docId, ...payload}) {
    try {
      const {riaId} = rootState;
      const sdkRequest = {riaId, payload: {id: docId, ...payload}};
      const fullPayload = addRiaComponentTo(sdkRequest, rootState.settings.default);
      const sdkResponse = await RwCloudConvertService.createDocVaultDocument(fullPayload).catch((error) => {
        const {microserviceStatusCode, message} = error;

        if (handledResponses.has(microserviceStatusCode)) {
          // eslint-disable-next-line no-param-reassign
          error.response = {status: microserviceStatusCode, message};
        }

        throw error;
      });

      return Promise.resolve(sdkResponse);
    } catch (error) {
      if (!error.response || (error.response && error.response.status !== 409)) {
        exceptionHandler.handleGenericException('documents.editDocument', error);
      }

      return Promise.reject(error);
    }
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async getExampleDocument({rootState}) {
    try {
      const sdkResponse = await RwCloudConvertService.getExampleDocument({}).catch((error) => {
        throw error;
      });

      return Promise.resolve(sdkResponse);
    } catch (error) {
      exceptionHandler.handleGenericException('documents.getExampleDocument', error);

      return Promise.reject(error);
    }
  },
  async getWorkflows({rootState}) {
    try {
      const {riaId} = rootState;
      const fullPayload = addRiaComponentTo({riaId}, rootState.settings.default);
      const sdkResponse = await RwCloudConvertService.getAllWorkFlowSummary(fullPayload).catch((error) => {
        throw error;
      });

      return Promise.resolve(sdkResponse);
    } catch (error) {
      exceptionHandler.handleGenericException('documents.getWorkflows', error);

      return Promise.reject(error);
    }
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async previewWorkflow({rootState}, {wfId, clUserId}) {
    try {
      const payload = {
        workflowId: wfId,
        clUserId,
      };

      const response = await RwCloudConvertService.getWorkflowDocumentPreview(payload);

      return response.data;
    } catch (error) {
      exceptionHandler.handleGenericException('documents.previewWorkflow', error);

      return Promise.reject(error);
    }
  },
  async getWorkflow({rootState}, {wfId}) {
    try {
      const {riaId} = rootState;
      const sdkPayload = {riaId, workflowId: wfId};
      const fullPayload = addRiaComponentTo(sdkPayload, rootState.settings.default);
      const sdkResponse = await RwCloudConvertService.getWorkflow(fullPayload).catch((error) => {
        throw error;
      });

      return Promise.resolve(sdkResponse);
    } catch (error) {
      exceptionHandler.handleGenericException('documents.getWorkflow', error);

      return Promise.reject(error);
    }
  },
  async saveWorkflow({rootState}, payload) {
    try {
      const {riaId} = rootState;
      const fullPayload = addRiaComponentTo({riaId, payload}, rootState.settings.default);
      const sdkResponse = await RwCloudConvertService.createWorkflow(fullPayload).catch((error) => {
        const {microserviceStatusCode, message} = error;

        if (handledResponses.has(microserviceStatusCode)) {
          // eslint-disable-next-line no-param-reassign
          error.response = {status: microserviceStatusCode, message};
        }

        throw error;
      });

      return Promise.resolve(sdkResponse);
    } catch (error) {
      if (!error.response || (error.response && error.response.status !== 409)) {
        exceptionHandler.handleGenericException('documents.saveWorkflow', error);
      }

      return Promise.reject(error);
    }
  },
  async editWorkflow({rootState}, {wfId, ...payload}) {
    try {
      const {riaId} = rootState;
      const sdkPayload = {riaId, workflowId: wfId, payload};
      const fullPayload = addRiaComponentTo(sdkPayload, rootState.settings.default);
      const sdkResponse = await RwCloudConvertService.updateWorkflow(fullPayload).catch((error) => {
        const {microserviceStatusCode, message} = error;

        if (handledResponses.has(microserviceStatusCode)) {
          // eslint-disable-next-line no-param-reassign
          error.response = {status: microserviceStatusCode, message};
        }

        throw error;
      });

      return Promise.resolve(sdkResponse);
    } catch (error) {
      if (!error.response || (error.response && error.response.status !== 409)) {
        exceptionHandler.handleGenericException('documents.editWorkflow', error);
      }

      return Promise.reject(error);
    }
  },
  async deleteWorkflow({rootState}, {wfId}) {
    try {
      const {riaId} = rootState;
      const sdkPayload = {riaId, workflowId: wfId};
      const fullPayload = addRiaComponentTo(sdkPayload, rootState.settings.default);
      const sdkResponse = await RwCloudConvertService.deleteWorkflow(fullPayload).catch((error) => {
        const {microserviceStatusCode, message} = error;

        if (handledResponses.has(microserviceStatusCode)) {
          // eslint-disable-next-line no-param-reassign
          error.response = {status: microserviceStatusCode, message};
        }

        throw error;
      });

      return Promise.resolve(sdkResponse);
    } catch (error) {
      if (!error.response || (error.response && error.response.status !== 409)) {
        exceptionHandler.handleGenericException('documents.deleteWorkflow', error);
      }

      return Promise.reject(error);
    }
  },
};

const mutations = {
  SET_DOCUMENTS(state, payload) {
    state.documents = payload;
  },
};

export default {
  namespaced: true,
  state: documentState,
  getters,
  actions,
  mutations,
};
